import { AttendRegisterDomainService } from './domain';
import { AttendRegisterFactory } from './entity/attendRegisterFactory';

import type { AttendRegisterJsonPub, AttendRegisterJsonPvt } from './entity/attendRegisterEntity';
import type { AttendRegisterIRepository } from './entity/attendRegisterIRepository';
import type { EntityDataPubType } from '@/domain/entityBase';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { SheetJson, SheetJsonPvt } from '@/types/domain/attendance/entities/sheetEntity';

import { SheetFactory } from '@/domain/attendance/service/entity/sheetFactory';

/**
 * AttendRegisterApplicationService
 */
export class AttendRegisterApplicationService {
  private service!: AttendRegisterDomainService;

  private attendRegisterFactory = new AttendRegisterFactory();

  private sheetFactory = new SheetFactory();

  /**
   * Injection
   * @param attendRegisterRepository attendRegisterRepository
   * @param logger logger
   */
  public inject(
    attendRegisterRepository: AttendRegisterIRepository,
    logger: Logger,
  ) {
    this.service = new AttendRegisterDomainService(logger);
    this.service.inject(attendRegisterRepository);
  }

  /**
   * getAttendRegisterMonthList
   * @param year 年
   * @param month 月
   * @param parentChildId parentChildId
   * @param token token
   * @returns アイテムリスト
   */
  async getAttendRegisterMonthList(
    year: number,
    month: number,
    parentChildId: string,
    token: string | null,
  ) {
    const items = await this.service.getAttendRegisterMonthList(
      year,
      month,
      parentChildId,
      token,
    );
    return items;
  }

  /**
   * 登園登録リストを更新する(Create)
   * @param attendRegisterList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createAttendRegisterStore(
    attendRegisterList: EntityDataPubType<AttendRegisterJsonPvt, AttendRegisterJsonPub>[],
    payload: EntitySubscribeType,
    filter: ((json: AttendRegisterJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      attendRegisterList,
      payload,
      this.attendRegisterFactory,
      filter,
    );
  }

  /**
   * 登園登録リストを更新する(Update)
   * @param attendRegisterList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateAttendRegisterStore(
    attendRegisterList: EntityDataPubType<AttendRegisterJsonPvt, AttendRegisterJsonPub>[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      attendRegisterList,
      payload,
      this.attendRegisterFactory,
    );
  }

  /**
   * 登園登録リストを更新する(Delete)
   * @param attendRegisterList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteAttendRegisterStore(
    attendRegisterList: EntityDataPubType<AttendRegisterJsonPvt, AttendRegisterJsonPub>[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      attendRegisterList,
      payload,
      this.attendRegisterFactory,
    );
  }

  /**
   * 出席簿リストを更新する(Create)
   * @param sheetList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createSheetStore(
    sheetList: SheetJson[],
    payload: EntitySubscribeType,
    filter: ((json: SheetJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      sheetList,
      payload,
      this.sheetFactory,
      filter,
    );
  }

  /**
   * 出席簿リストを更新する(Update)
   * @param sheetList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateSheetStore(
    sheetList: SheetJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      sheetList,
      payload,
      this.sheetFactory,
    );
  }

  /**
   * 出席簿リストを更新する(Delete)
   * @param sheetList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteSheetStore(
    sheetList: SheetJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      sheetList,
      payload,
      this.sheetFactory,
    );
  }
}
